import { useEffect, useState } from "react";

/**
 * Watches a fixed list of section IDs on the current page and returns the ID
 * of whichever section is currently "in view" — meaning whichever has the
 * largest visible area inside the upper-middle band of the viewport. Returns
 * an empty string when nothing matches (e.g. above the hero).
 *
 * `ids` must be referentially stable (declare it outside the component or
 * with `useMemo`), otherwise the observer will be torn down every render.
 */
export function useActiveSection(ids: readonly string[]): string {
  const [active, setActive] = useState<string>("");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return undefined;

    const visibility = new Map<string, number>();
    elements.forEach((el) => visibility.set(el.id, 0));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.intersectionRatio);
        }
        // Pick the most-visible section. If everything is at 0 we
        // intentionally clear the active state so nothing is highlighted at
        // the top of the page.
        let bestId = "";
        let bestRatio = 0;
        visibility.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestId = id;
            bestRatio = ratio;
          }
        });
        setActive(bestRatio > 0 ? bestId : "");
      },
      {
        // Trigger band: top 20% to top 50% of the viewport.
        rootMargin: "-20% 0px -50% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
